import './../clientProfile.scss';
import { Button } from 'react-bootstrap';
import { getAppendicesFile } from '../../../utils/api/insuranceAppendix';
import { useContext, useEffect, useState } from 'react';
import LaddaButton, { EXPAND_LEFT } from 'react-ladda-button';
import { downloadFileFromAPI } from '../../../utils/downloadFileFromAPI';
import DualSigningButton from '../../../components/dualSigningButton/DualSigningButton';
import { archiveSignature, getSignatures } from '../../../utils/api/signatures';
import { DocumentType, SignatureStatus, SignedDocument } from '../../../interfaces/SignedDocument';
import { generateInsuranceForms, generateInsuranceHatama } from '../../../utils/api/insurancePolicy';
import { UserProfileContext } from '../../../context/userProfileContext';
import { useTranslation } from 'react-i18next';
import globalErrorHandler from '../../../utils/globalErrorHandler';
import { InsurancePolicy, InsurancePolicyStatuses } from '../../../interfaces/InsurancePolicy';
import { InsuranceRecommendation } from '../../../interfaces/Insurance';

interface InsuranceProductsMenuHeaderProps {
    setShowNewProductCreationModal: (show: boolean) => void;
    clientId: string;
    clientFullName: string;
    getClientsPolicies: () => void;
    showGenerateForms: boolean;
    showQuestionnaireForms: boolean;
    policies: InsurancePolicy[];
}

const InsuranceProductsMenuHeader = ({
    setShowNewProductCreationModal,
    clientId,
    clientFullName,
    getClientsPolicies,
    showGenerateForms,
    showQuestionnaireForms,
    policies,
}: InsuranceProductsMenuHeaderProps) => {
    const { profileContextData } = useContext(UserProfileContext);
    const { client, currentRound } = profileContextData;
    const { t } = useTranslation('insurance');

    const [isDownloading, setIsDownloading] = useState(false);
    const [isDownloadingHatama, setIsDownloadingHatama] = useState(false);
    const [signedDocument, setSignedDocument] = useState<SignedDocument>();
    const [hasPoliciesToTransfer, setHasPoliciesToTransfer] = useState(false);
    const [questionnaireSignedDocument, setQuestionnaireSignedDocument] = useState<SignedDocument>();

    const hasRelevantPolicies = policies.some(
        (p) =>
            p.isNewPolicy ||
            p.isAgentAppointed ||
            p.recommendation === InsuranceRecommendation.fullCancellation ||
            p.recommendation === InsuranceRecommendation.partialCancellation
    );

    const handleDownloadHatama = async () => {
        if (!currentRound) return;

        setIsDownloadingHatama(true);
        try {
            const response = await generateInsuranceHatama({ insuranceId: currentRound._id });
            const fileName = clientFullName + '_hatama_' + new Date().toLocaleDateString();
            downloadFileFromAPI(response, fileName, 'pdf');
        } catch (err: any) {
            globalErrorHandler(err);
        } finally {
            setIsDownloadingHatama(false);
        }
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const response = await getAppendicesFile({ clientId });
            const fileName = clientFullName + '_appendices_' + new Date().toLocaleDateString();
            downloadFileFromAPI(response, fileName, 'xlsx');
        } finally {
            setIsDownloading(false);
        }
    };

    const generateForms = async (documentType: DocumentType, preview: boolean) => {
        if (!currentRound) {
            return;
        }

        try {
            const result = await generateInsuranceForms({
                insuranceId: currentRound._id,
                documentType,
                preview,
            });
            if (preview) return result;

            if (documentType === DocumentType.Insurance) setSignedDocument(result.signedDocument);
            else if (documentType === DocumentType.InsuranceQuestionnaire) setQuestionnaireSignedDocument(result.signedDocument);

            // Reload policies after forms are generated
            await getClientsPolicies();
        } catch (err: any) {
            globalErrorHandler(err);
        }
    };

    const archiveSigningDocument = async (signedDoc: SignedDocument) => {
        if (!window.confirm('Are you sure you want to archive this document?')) return;

        try {
            const result = await archiveSignature(signedDoc._id);
            if (signedDoc === signedDocument) setSignedDocument(undefined);
            else if (signedDoc === questionnaireSignedDocument) setQuestionnaireSignedDocument(undefined);

            await getClientsPolicies();
        } catch (err: any) {
            globalErrorHandler(err);
        }
    };

    useEffect(() => {
        const hasPoliciesToTransfer = policies.some((p) => p.status !== InsurancePolicyStatuses.Initial);
        setHasPoliciesToTransfer(hasPoliciesToTransfer);
    }, [policies]);

    useEffect(() => {
        const getSignedDocs = async () => {
            try {
                const { signedDocuments: signedDocumentsClient } = await getSignatures({
                    clientId: client._id,
                    documentType: 'insurance',
                    insuranceId: currentRound?._id,
                });

                const { signedDocuments: signedDocumentsQuestionnaire } = await getSignatures({
                    clientId: client._id,
                    documentType: 'insuranceQuestionnaire',
                    insuranceId: currentRound?._id,
                });

                if (signedDocumentsClient.length) setSignedDocument(signedDocumentsClient[0]);
                if (signedDocumentsQuestionnaire.length) setQuestionnaireSignedDocument(signedDocumentsQuestionnaire[0]);
            } catch (error) {
                globalErrorHandler(error);
            }
        };
        getSignedDocs();
    }, [client, currentRound]);

    const getNewPolicyText = () => {
        const selectedPolicies = policies.filter((p) => p.isSelected);
        if (selectedPolicies.length === 0) return t('New Policy');
        if (selectedPolicies.length === 1) return t('Replace Policy');
        return `${t('Replace')} ${selectedPolicies.length} ${t('Policies')}`;
    };

    return (
        <div className="d-flex flex-row-reverse">
            <div className="p-2">
                <Button onClick={() => setShowNewProductCreationModal(true)}>{getNewPolicyText()}</Button>
            </div>
            <div className="p-2">
                <LaddaButton
                    loading={isDownloading}
                    onClick={handleDownload}
                    data-color="#eee"
                    data-style={EXPAND_LEFT}
                    data-spinner-size={30}
                    data-spinner-color="#ddd"
                    data-spinner-lines={12}
                    className="btn btn-primary"
                >
                    <i className="mdi mdi-download"></i> Download
                </LaddaButton>
            </div>
            {hasRelevantPolicies && (
                <div className="p-2">
                    <LaddaButton
                        loading={isDownloadingHatama}
                        onClick={handleDownloadHatama}
                        data-color="#eee"
                        data-style={EXPAND_LEFT}
                        data-spinner-size={30}
                        data-spinner-color="#ddd"
                        data-spinner-lines={12}
                        className="btn btn-info"
                    >
                        <i className="mdi mdi-file-document"></i> {t('Download Needs Assessment')}
                    </LaddaButton>
                </div>
            )}
            {(showGenerateForms || signedDocument) && (
                <div className="p-2">
                    <DualSigningButton
                        onArchive={signedDocument ? () => archiveSigningDocument(signedDocument) : undefined}
                        text={t('Insurance forms')}
                        signedDocument={signedDocument}
                        isEnabled={!signedDocument}
                        generateSigningLink={(preview: boolean) => generateForms(DocumentType.Insurance, preview)}
                        onError={(err: any) => alert("Can't generate insurance forms")}
                    />
                </div>
            )}

            {((showQuestionnaireForms && signedDocument?.signatureStatus === SignatureStatus.ActiveAgentFinished) || questionnaireSignedDocument) && (
                <div className="p-2">
                    <DualSigningButton
                        onArchive={questionnaireSignedDocument ? () => archiveSigningDocument(questionnaireSignedDocument) : undefined}
                        text={t('Questionnaires')}
                        signedDocument={questionnaireSignedDocument}
                        isEnabled={!questionnaireSignedDocument}
                        generateSigningLink={(preview: boolean) => generateForms(DocumentType.InsuranceQuestionnaire, preview)}
                        onError={(err: any) => alert("Can't generate questionnaires")}
                    />
                </div>
            )}
        </div>
    );
};

export default InsuranceProductsMenuHeader;
